import React, { useState, useEffect, useCallback } from 'react';
import { blogApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * The member blog that lives inside Help.
 *
 * Posts are plain text rendered as text — never HTML — so nothing a member
 * writes can execute. Visibility is decided on the server: this panel can only
 * ever show what the blog endpoint already approved.
 */

const STATUS_STYLE = {
  pending:  { label: 'Awaiting review', color: 'var(--amber)', bg: 'var(--amber-bg)' },
  approved: { label: 'Published',       color: 'var(--green)', bg: 'var(--green-bg)' },
  rejected: { label: 'Not accepted',    color: 'var(--red)',   bg: 'var(--red-bg)'   },
};

const when = (iso) => new Date(iso).toLocaleDateString(undefined,
  { year: 'numeric', month: 'short', day: 'numeric' });

const errText = (e, fallback) =>
  e?.response?.data?.message || e?.response?.data?.error || fallback;

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { label: status, color: 'var(--text3)', bg: 'transparent' };
  return (
    <span style={{
      fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99,
      textTransform: 'uppercase', letterSpacing: '0.05em',
      border: `1px solid ${s.color}`, background: s.bg, color: s.color,
    }}>{s.label}</span>
  );
}

function PostBody({ children }) {
  return (
    <div style={{
      fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 6,
    }}>{children}</div>
  );
}

export default function BlogPanel() {
  const { user } = useAuth();
  const [view,   setView]   = useState('list');
  const [posts,  setPosts]  = useState([]);
  const [mine,   setMine]   = useState([]);
  const [gate,   setGate]   = useState(null);
  const [queue,  setQueue]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [error,  setError]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [blogApi.published(), blogApi.mine(), blogApi.canPost()];
      if (user?.admin) calls.push(blogApi.queue());
      const [p, m, c, q] = await Promise.all(calls);
      setPosts(p.data); setMine(m.data); setGate(c.data);
      if (q) setQueue(q.data);
    } catch (e) {
      setError(errText(e, 'Could not load the blog.'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = queue.filter(p => p.status === 'pending').length;

  const TABS = [
    { id: 'list',  label: `Posts${posts.length ? ` (${posts.length})` : ''}` },
    { id: 'write', label: 'Write' },
    { id: 'mine',  label: `Mine${mine.length ? ` (${mine.length})` : ''}` },
    ...(user?.admin ? [{ id: 'moderate', label: `Moderate${pendingCount ? ` (${pendingCount})` : ''}` }] : []),
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} className={`btn btn-sm${view === t.id ? ' btn-primary' : ''}`}
            onClick={() => { setView(t.id); setNotice(null); setError(null); }}>{t.label}</button>
        ))}
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
      {notice && <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>{notice}</div>}

      {loading ? <div className="spinner" /> : (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {view === 'list' && (
            <PublishedList posts={posts} onReported={(msg) => { setNotice(msg); load(); }}
              onError={setError} />
          )}
          {view === 'write' && (
            <Composer gate={gate}
              onDone={(msg) => { setNotice(msg); setView('mine'); load(); }}
              onError={setError} />
          )}
          {view === 'mine' && <MyPosts posts={mine} />}
          {view === 'moderate' && user?.admin && (
            <ModerationQueue queue={queue}
              onChanged={(msg) => { setNotice(msg); load(); }} onError={setError} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Reading ──────────────────────────────────────────────────────────────────

function PublishedList({ posts, onReported, onError }) {
  const [reporting, setReporting] = useState(null);

  const report = async (post) => {
    if (!window.confirm('Report this post for a moderator to look at again?')) return;
    setReporting(post.id);
    try {
      const res = await blogApi.report(post.id);
      onReported(res.data.removed
        ? 'Reported — enough readers flagged it that it has come down pending re-review.'
        : 'Reported. A moderator will take another look.');
    } catch (e) {
      onError(errText(e, 'Could not report that post.'));
    } finally {
      setReporting(null);
    }
  };

  if (posts.length === 0) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>
        No posts yet. Every post here is read by a moderator before it appears —
        write the first one from the <strong>Write</strong> tab.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {posts.map(p => (
        <article key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13.5, color: 'var(--text)' }}>{p.title}</strong>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              {p.authorAlias} · {when(p.createdAt)}{p.mine ? ' · yours' : ''}
            </span>
            {!p.mine && (
              <button className="btn btn-sm" style={{ marginLeft: 'auto', fontSize: 10.5 }}
                disabled={reporting === p.id} onClick={() => report(p)}>
                {reporting === p.id ? '…' : 'Report'}
              </button>
            )}
          </div>
          <PostBody>{p.body}</PostBody>
        </article>
      ))}
    </div>
  );
}

// ── Writing ──────────────────────────────────────────────────────────────────

function Composer({ gate, onDone, onError }) {
  const [title, setTitle] = useState('');
  const [alias, setAlias] = useState('');
  const [body,  setBody]  = useState('');
  const [busy,  setBusy]  = useState(false);
  const [refused, setRefused] = useState(null);

  if (gate && !gate.allowed) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>
        {gate.reason}
      </div>
    );
  }

  const submit = async () => {
    if (!title.trim() || !body.trim()) return;
    setBusy(true); setRefused(null);
    try {
      const res = await blogApi.submit({ title, body, authorAlias: alias });
      if (res.data.status === 'rejected') {
        // Kept on screen deliberately — the author should be able to edit and
        // resubmit rather than lose what they wrote.
        setRefused(res.data.message);
      } else {
        onDone(res.data.message);
      }
    } catch (e) {
      onError(errText(e, 'Could not submit that post.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{
        fontSize: 11.5, color: 'var(--text2)', lineHeight: 1.55, marginBottom: 14,
        padding: '9px 11px', border: '1px solid var(--border)', borderRadius: 8,
      }}>
        Posts are screened automatically and then <strong>read by a moderator before
        anyone else sees them</strong>. Keep it about budgeting. No abuse, no slurs,
        no financial pitches — those are refused outright. Never post account
        numbers, balances you'd rather not share, or anything that identifies you
        beyond the display name you choose.
      </div>

      {refused && (
        <div style={{
          fontSize: 12, color: 'var(--red)', marginBottom: 12, padding: '9px 11px',
          border: '1px solid var(--red)', background: 'var(--red-bg)', borderRadius: 8,
        }}>{refused}</div>
      )}

      <div className="form-group" style={{ marginBottom: 10 }}>
        <label>Title</label>
        <input className="field-input" style={{ width: '100%' }} maxLength={200}
          value={title} onChange={e => setTitle(e.target.value)} />
      </div>
      <div className="form-group" style={{ marginBottom: 10 }}>
        <label>Display name</label>
        <input className="field-input" style={{ width: '100%' }} maxLength={40}
          placeholder="Member" value={alias} onChange={e => setAlias(e.target.value)} />
        <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>
          Shown on the post. Your email is never published.
        </div>
      </div>
      <div className="form-group" style={{ marginBottom: 14 }}>
        <label>Post</label>
        <textarea className="field-input" rows={8} maxLength={8000}
          style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
          value={body} onChange={e => setBody(e.target.value)} />
        <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>
          {body.length} / 8000
        </div>
      </div>
      <button className="btn btn-primary" disabled={busy || !title.trim() || !body.trim()}
        onClick={submit}>
        {busy ? 'Submitting…' : 'Submit for review'}
      </button>
    </div>
  );
}

function MyPosts({ posts }) {
  if (posts.length === 0) {
    return <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>You haven't written anything yet.</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {posts.map(p => (
        <div key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13.5 }}>{p.title}</strong>
            <StatusPill status={p.status} />
            <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>{when(p.createdAt)}</span>
          </div>
          {p.moderatorNote && (
            <div style={{ fontSize: 11.5, color: 'var(--amber)', marginTop: 6 }}>{p.moderatorNote}</div>
          )}
          {p.status === 'rejected' && p.screenNote && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>Screen: {p.screenNote}</div>
          )}
          <PostBody>{p.body}</PostBody>
        </div>
      ))}
    </div>
  );
}

// ── Moderation ───────────────────────────────────────────────────────────────

function ModerationQueue({ queue, onChanged, onError }) {
  const [filter, setFilter] = useState('pending');
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});

  const shown = queue.filter(p => filter === 'all' || p.status === filter);

  const act = async (post, status) => {
    setBusy(post.id);
    try {
      await blogApi.moderate(post.id, status, notes[post.id] || '');
      onChanged(status === 'approved' ? 'Published.' : 'Kept off the blog.');
    } catch (e) {
      onError(errText(e, 'Could not update that post.'));
    } finally { setBusy(null); }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}" permanently? This cannot be undone.`)) return;
    setBusy(post.id);
    try {
      await blogApi.remove(post.id);
      onChanged('Deleted.');
    } catch (e) {
      onError(errText(e, 'Could not delete that post.'));
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {['pending', 'approved', 'rejected', 'all'].map(f => (
          <button key={f} className={`btn btn-sm${filter === f ? ' btn-primary' : ''}`}
            style={{ textTransform: 'capitalize' }} onClick={() => setFilter(f)}>{f}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>Nothing {filter === 'all' ? 'here' : `${filter}`}.</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {shown.map(p => (
            <div key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 13.5 }}>{p.title}</strong>
                <StatusPill status={p.status} />
                {p.reportCount > 0 && (
                  <span style={{ fontSize: 10.5, color: 'var(--red)' }}>
                    {p.reportCount} report{p.reportCount === 1 ? '' : 's'}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>
                  {p.authorAlias} · {p.authorEmail} · {when(p.createdAt)}
                </span>
              </div>

              {p.screenNote && (
                <div style={{
                  fontSize: 11, marginTop: 7, padding: '6px 9px', borderRadius: 6,
                  border: '1px solid var(--amber)', background: 'var(--amber-bg)', color: 'var(--amber)',
                }}>
                  Screen flagged: {p.screenNote}
                  {p.screenMatches && <> — <em>{p.screenMatches}</em></>}
                </div>
              )}

              <PostBody>{p.body}</PostBody>

              <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input className="field-input" placeholder="Note to the author (optional)"
                  style={{ flex: '1 1 200px', fontSize: 11.5 }} maxLength={500}
                  value={notes[p.id] || ''}
                  onChange={e => setNotes(n => ({ ...n, [p.id]: e.target.value }))} />
                {p.status !== 'approved' && (
                  <button className="btn btn-primary btn-sm" disabled={busy === p.id}
                    onClick={() => act(p, 'approved')}>Publish</button>
                )}
                {p.status !== 'rejected' && (
                  <button className="btn btn-sm" disabled={busy === p.id}
                    onClick={() => act(p, 'rejected')}>Reject</button>
                )}
                <button className="btn btn-danger btn-sm" disabled={busy === p.id}
                  onClick={() => remove(p)}>Delete</button>
              </div>

              {p.reviewedBy && (
                <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 6 }}>
                  Last reviewed by {p.reviewedBy}{p.reviewedAt ? ` on ${when(p.reviewedAt)}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
